import { useEffect, useMemo, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { useBlocker } from 'react-router-dom';
import { dangerButton, dialogBackdrop, dialogPanel, outlineButton } from './styles';
import { GuardContext } from './useUnsavedChanges';
import type { Guard } from './useUnsavedChanges';
import { isSectionRootNavigation } from './sectionRootNavigation';

/**
 * Leaving an editor with unsaved changes asks "Discard your changes?" first:
 * in-app navigation (links, Back and Forward) waits for the answer, and
 * closing or reloading the tab brings up the browser's own leave prompt.
 * One guard for the whole app, so there is one blocker however many editors
 * are open; each editor takes part through useUnsavedChanges. It must sit
 * inside a data router (useBlocker needs one).
 */
export function UnsavedChangesGuard({ children }: { children: ReactNode }) {
  // One authority for both protections: all entries prevent unload, while only
  // navigation-scoped entries block leaving/resetting the current page.
  const changed = useRef(new Map<symbol, boolean>());
  const [anyChanged, setAnyChanged] = useState(false);
  const [pending, setPending] = useState<{ leave: () => void } | null>(null);
  // A page/query change or explicit sidebar root reset leaves the editors; a hash change does not.
  const blocker = useBlocker(
    ({ currentLocation, nextLocation, historyAction }) =>
      [...changed.current.values()].some((navigation) => navigation) &&
      (currentLocation.pathname !== nextLocation.pathname || currentLocation.search !== nextLocation.search ||
        (historyAction !== 'POP' && isSectionRootNavigation(nextLocation.state))),
  );

  useEffect(() => {
    if (!anyChanged) return;
    const warn = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      // Older browsers show their prompt only when returnValue is set.
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [anyChanged]);

  const guard = useMemo<Guard>(
    () => ({
      mark: (editor, isChanged, navigation) => {
        if (isChanged) changed.current.set(editor, navigation);
        else changed.current.delete(editor);
        setAnyChanged(changed.current.size > 0);
      },
      ask: (leave) => setPending({ leave }),
    }),
    [],
  );

  const blocked = blocker.state === 'blocked';
  const keep = () => {
    if (blocked) blocker.reset();
    setPending(null);
  };
  const discard = () => {
    if (blocked) {
      blocker.proceed();
      return;
    }
    const leave = pending?.leave;
    setPending(null);
    leave?.();
  };

  return (
    <GuardContext.Provider value={guard}>
      {children}
      {(blocked || pending) && <DiscardDialog onKeep={keep} onDiscard={discard} />}
    </GuardContext.Provider>
  );
}

function DiscardDialog({ onKeep, onDiscard }: { onKeep: () => void; onDiscard: () => void }) {
  const keepButton = useRef<HTMLButtonElement>(null);
  useEffect(() => keepButton.current?.focus(), []);
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onKeep();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onKeep]);

  return (
    <div className={dialogBackdrop} onClick={onKeep}>
      <div
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="discard-title"
        aria-describedby="discard-body"
        className={dialogPanel}
        onClick={(event) => event.stopPropagation()}
      >
        <h2 id="discard-title" className="text-lg font-semibold mb-2">
          Discard your changes?
        </h2>
        <p id="discard-body" className="text-sm text-on-surface-variant mb-4">
          What you changed here has not been saved.
        </p>
        <div className="flex flex-wrap gap-2">
          <button type="button" ref={keepButton} onClick={onKeep} className={outlineButton}>
            Keep editing
          </button>
          <button type="button" onClick={onDiscard} className={dangerButton}>
            Discard
          </button>
        </div>
      </div>
    </div>
  );
}
